/**
 * db:// tag-path grammar — the client-side twin of proxy-server's
 * `internal/ontology/tag/db_path.go`. A database data point is a SQL query
 * whose result rides the tag pipeline, registered under the identity:
 *
 *   tag_path = db://{connection}/{datapoint}
 *
 * This is a fourth tag-identity flavor beside the Ignition `[provider]path`
 * form, the exposed-tags `nsu=` node id, and the `opc://` WebDev relay. The
 * grammar here (build/parse/detect) must stay in lockstep with the Go twin —
 * see proxy-server/CLAUDE.md and axilon-apps/CLAUDE.md.
 */

/** Prefix that marks a registry tag path as a database data point. */
export const DB_TAG_PATH_PREFIX = 'db://'

/** The two halves of a parsed `db://{connection}/{datapoint}` path. */
export interface ParsedDbTagPath {
  /** The connection segment (never contains a slash). */
  connection: string
  /**
   * The datapoint segment, kept verbatim after the first slash — it may
   * itself contain slashes (e.g. `schema/table/col`).
   */
  datapoint: string
}

/**
 * Reports whether `path` is a database data-point identity
 * (`db://{connection}/{datapoint}`). The prefix match is case-insensitive to
 * mirror the Go `IsDBTagPath`; the rest of the path is left verbatim.
 */
export function isDbTagPath(path: string): boolean {
  return path.trim().toLowerCase().startsWith(DB_TAG_PATH_PREFIX)
}

/**
 * Splits a db tag path into its connection and datapoint. The datapoint is
 * kept verbatim after the first slash (it may contain further slashes).
 * Returns `null` when `path` is not a db path or is missing either half —
 * matching the Go `ParseDBTagPath` rejection rules.
 */
export function parseDbTagPath(path: string): ParsedDbTagPath | null {
  const trimmed = path.trim()
  if (!isDbTagPath(trimmed)) return null

  const rest = trimmed.slice(DB_TAG_PATH_PREFIX.length)
  const slash = rest.indexOf('/')
  // slash must exist, not be leading (empty connection), and not be trailing
  // (empty datapoint).
  if (slash <= 0 || slash === rest.length - 1) return null

  const connection = rest.slice(0, slash).trim()
  const datapoint = rest.slice(slash + 1).trim()
  if (connection === '' || datapoint === '') return null

  return { connection, datapoint }
}

/**
 * Builds a `db://{connection}/{datapoint}` tag path. The datapoint may contain
 * slashes, but the connection may not (the first slash delimits the two
 * halves). Throws when either half is empty after trimming or the connection
 * contains a slash — so a caller can never mint a path that `parseDbTagPath`
 * would reject.
 */
export function buildDbTagPath(connection: string, datapoint: string): string {
  const conn = connection.trim()
  const point = datapoint.trim()
  if (conn === '') {
    throw new Error('buildDbTagPath: connection must not be empty')
  }
  if (conn.includes('/')) {
    throw new Error(`buildDbTagPath: connection must not contain a slash: ${connection}`)
  }
  if (point === '') {
    throw new Error('buildDbTagPath: datapoint must not be empty')
  }
  return `${DB_TAG_PATH_PREFIX}${conn}/${point}`
}
